import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import ImageNotSupportedRoundedIcon from '@mui/icons-material/ImageNotSupportedRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import DashboardPageHeader from 'pages/dashboard/DashboardPageHeader';
import VendorDashboardLayout from 'pages/dashboard/VendorDashboardLayout';

const PAGE_SIZE = 20;
const emptyFilters = {
  subCategoryId: '',
  materialId: '',
  productTypeId: '',
  productCode: '',
  status: ''
};

const sortByLandingPosition = (list) =>
  [...list].sort((a, b) => (a.position_landing ?? 1000000000) - (b.position_landing ?? 1000000000));

const isKidsSubcategory = (subcategory) =>
  Number(subcategory.categoryId) === 2 || /kids|беб|дет/i.test(subcategory.name_bg || '');

const sortSubcategories = (list) =>
  [...list].sort((first, second) => {
    const groupDifference = Number(isKidsSubcategory(first)) - Number(isKidsSubcategory(second));
    if (groupDifference) return groupDifference;

    const positionDifference =
      (first.position ?? Number.MAX_SAFE_INTEGER) - (second.position ?? Number.MAX_SAFE_INTEGER);
    if (positionDifference) return positionDifference;

    return String(first.name_bg || '').localeCompare(String(second.name_bg || ''), 'bg');
  });

const statusPresentation = (status) =>
  status === 'draft'
    ? { label: 'Чернова', color: 'error', background: '#991b1b', textColor: '#ffffff' }
    : { label: 'Публикуван', color: 'success', background: '#15803d', textColor: '#ffffff' };

function ProductImage({ product, size = 68, to }) {
  const imageUrl = product?.images?.[0]?.url;
  const image = imageUrl ? (
    <Box
      component="img"
      src={imageUrl}
      alt={product.name_bg || 'Продукт'}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2.5,
        display: 'block',
        flexShrink: 0,
        height: size,
        objectFit: 'cover',
        width: size
      }}
    />
  ) : (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: 'grey.100',
        borderRadius: 2.5,
        color: 'grey.400',
        display: 'flex',
        flexShrink: 0,
        height: size,
        justifyContent: 'center',
        width: size
      }}
    >
      <ImageNotSupportedRoundedIcon />
    </Box>
  );

  if (!to) return image;

  return (
    <Box
      component={Link}
      to={to}
      aria-label={`Редактирай ${product.name_bg || `продукт ${product.id}`}`}
      sx={{
        borderRadius: 2.5,
        display: 'inline-flex',
        flexShrink: 0,
        transition: 'transform 150ms ease, box-shadow 150ms ease',
        '&:hover': {
          boxShadow: '0 7px 18px rgba(15, 23, 42, .16)',
          transform: 'translateY(-2px)'
        },
        '&:focus-visible': {
          boxShadow: '0 0 0 3px rgba(79, 70, 229, .28)',
          outline: 'none'
        }
      }}
    >
      {image}
    </Box>
  );
}

export default function Products() {
  const [subcategories, setSubcategories] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const updateFilter = (field) => (event) => {
    setFilters((current) => ({ ...current, [field]: event.target.value }));
  };

  const fetchProducts = async (page = 0, append = false, activeFilters = filters) => {
    setIsLoading(true);
    setError('');
    try {
      const [materialId, materialVariant, materialThreadCount] = String(
        activeFilters.materialId || ''
      ).split(':');
      const { data } = await axios.get('/products/', {
        params: {
          page,
          subCategoryId: activeFilters.subCategoryId || undefined,
          materialId: materialId || undefined,
          materialThreadCount: materialVariant === 'tc' ? materialThreadCount : undefined,
          productTypeId: activeFilters.productTypeId || undefined,
          productCode: activeFilters.productCode.trim() || undefined,
          status: activeFilters.status || undefined
        }
      });

      const prepared = data.map((product) => ({
        ...product,
        images: [...(product.images || [])].sort(
          (a, b) => a.Product_Image.position - b.Product_Image.position
        )
      }));

      setProducts((current) => {
        const nextProducts = sortByLandingPosition(append ? [...current, ...prepared] : prepared);
        localStorage.setItem('products', JSON.stringify(nextProducts));
        return nextProducts;
      });
      setHasMore(data.length === PAGE_SIZE);
      setCurrentPage(page);
    } catch (requestError) {
      setError('Продуктите не можаха да бъдат заредени. Моля опитайте отново.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const [subcategoryResponse, materialResponse, productTypeResponse] = await Promise.all([
        axios.get('/subcategory'),
        axios.get('/material', { params: { includeVariants: true } }),
        axios.get('/productType')
      ]);
      setSubcategories(sortSubcategories(subcategoryResponse.data));
      setMaterials(materialResponse.data);
      setProductTypes(productTypeResponse.data);
    } catch (requestError) {
      setError('Филтрите не можаха да бъдат заредени.');
    }
  };

  useEffect(() => {
    const cachedProducts = localStorage.getItem('products');
    if (cachedProducts) {
      try {
        setProducts(JSON.parse(cachedProducts));
      } catch (parseError) {
        localStorage.removeItem('products');
      }
    }
    fetchFilterOptions();
    fetchProducts(0, false, emptyFilters);
  }, []);

  const applyFilters = () => fetchProducts(0, false, filters);

  const resetFilters = () => {
    setFilters(emptyFilters);
    fetchProducts(0, false, emptyFilters);
  };

  const loadMore = () => fetchProducts(currentPage + 1, true, filters);
  const mainSubcategories = subcategories.filter((item) => !isKidsSubcategory(item));
  const kidsSubcategories = subcategories.filter(isKidsSubcategory);

  const subcategoryHeaderStyles = {
    bgcolor: 'grey.100',
    color: 'text.secondary',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.08em',
    lineHeight: '32px',
    textTransform: 'uppercase'
  };

  return (
    <VendorDashboardLayout>
      <DashboardPageHeader
        title="Продукти"
        subtitle="Търсете, филтрирайте и управлявайте продуктовия каталог."
        icon={Inventory2RoundedIcon}
      />

      <Card sx={{ mb: 3, overflow: 'visible' }}>
        <Box sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack direction="row" alignItems="center" gap={1} mb={2.5}>
            <FilterAltRoundedIcon color="primary" fontSize="small" />
            <Typography variant="h6">Филтри</Typography>
          </Stack>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4} lg={3}>
              <TextField
                fullWidth
                label="Код на продукта"
                placeholder="Напр. R-345"
                value={filters.productCode}
                onChange={updateFilter('productCode')}
                onKeyDown={(event) => event.key === 'Enter' && applyFilters()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon color="action" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.25}>
              <FormControl fullWidth>
                <InputLabel>Подкатегория</InputLabel>
                <Select
                  label="Подкатегория"
                  value={filters.subCategoryId}
                  onChange={updateFilter('subCategoryId')}
                >
                  <MenuItem value="">Всички</MenuItem>
                  {!!mainSubcategories.length && (
                    <ListSubheader disableSticky sx={subcategoryHeaderStyles}>
                      Основни
                    </ListSubheader>
                  )}
                  {mainSubcategories.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name_bg}
                    </MenuItem>
                  ))}
                  {!!kidsSubcategories.length && (
                    <ListSubheader
                      disableSticky
                      sx={{
                        ...subcategoryHeaderStyles,
                        borderColor: 'divider',
                        borderTop: '1px solid',
                        mt: 0.5,
                        pt: 0.5
                      }}
                    >
                      Детски
                    </ListSubheader>
                  )}
                  {kidsSubcategories.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name_bg}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.25}>
              <FormControl fullWidth>
                <InputLabel>Материал</InputLabel>
                <Select
                  label="Материал"
                  value={filters.materialId}
                  onChange={updateFilter('materialId')}
                >
                  <MenuItem value="">Всички</MenuItem>
                  {materials.map((item) => (
                    <MenuItem key={item.id} value={item.filterValue || item.id}>
                      {item.name_bg}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.25}>
              <FormControl fullWidth>
                <InputLabel>Тип продукт</InputLabel>
                <Select
                  label="Тип продукт"
                  value={filters.productTypeId}
                  onChange={updateFilter('productTypeId')}
                >
                  <MenuItem value="">Всички</MenuItem>
                  {productTypes.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name_bg}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.25}>
              <FormControl fullWidth>
                <InputLabel>Статус</InputLabel>
                <Select label="Статус" value={filters.status} onChange={updateFilter('status')}>
                  <MenuItem value="">Всички</MenuItem>
                  <MenuItem value="published">Публикувани</MenuItem>
                  <MenuItem value="draft">Чернови</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.25} mt={2.5}>
            <Button
              variant="contained"
              startIcon={<FilterAltRoundedIcon />}
              onClick={applyFilters}
              disabled={isLoading}
            >
              Приложи филтрите
            </Button>
            <Button
              startIcon={<RestartAltRoundedIcon />}
              onClick={resetFilters}
              disabled={isLoading}
            >
              Изчисти
            </Button>
          </Stack>
        </Box>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Box>
          <Typography variant="h6">Каталог</Typography>
          <Typography variant="body2" color="text.secondary">
            Показани {products.length} продукта
          </Typography>
        </Box>
        {isLoading && <CircularProgress size={24} />}
      </Stack>

      <Paper variant="outlined" sx={{ display: { xs: 'none', md: 'block' }, overflow: 'hidden' }}>
        <Box
          sx={{
            bgcolor: '#fafbfc',
            display: 'grid',
            gap: 2,
            gridTemplateColumns: '72px minmax(220px, 2fr) 110px 70px 120px 125px 110px',
            px: 2.5,
            py: 1.5
          }}
        >
          {['', 'Продукт', 'Код', 'ID', 'Позиция', 'Статус', 'Действия'].map((label) => (
            <Typography
              key={label || 'image'}
              variant="caption"
              sx={{ color: 'text.secondary', fontWeight: 750, letterSpacing: 0.5 }}
            >
              {label}
            </Typography>
          ))}
        </Box>
        {products.map((product) => {
          const status = statusPresentation(product.status || 'published');
          return (
            <Box
              key={product.id}
              sx={{
                alignItems: 'center',
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'grid',
                gap: 2,
                gridTemplateColumns: '72px minmax(220px, 2fr) 110px 70px 120px 125px 110px',
                px: 2.5,
                py: 1.5,
                transition: 'background-color 150ms ease',
                '&:hover': { bgcolor: '#fafbff' }
              }}
            >
              <ProductImage product={product} to={`/productDetails/${product.id}`} />
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={680} noWrap>
                  {product.name_bg}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {product.name_en || product.slug_bg}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={650}>
                {product.code}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                #{product.id}
              </Typography>
              <Typography variant="body2">
                {product.position_landing ?? (
                  <Box component="span" color="text.secondary">
                    —
                  </Box>
                )}
              </Typography>
              <Chip
                label={status.label}
                color={status.color}
                size="small"
                sx={{
                  bgcolor: status.background,
                  color: status.textColor,
                  justifySelf: 'start'
                }}
              />
              <Stack direction="row">
                <Tooltip title="Редактирай">
                  <IconButton component={Link} to={`/productDetails/${product.id}`} color="primary">
                    <EditRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Клонирай">
                  <IconButton component={Link} to={`/cloneProduct/${product.id}`}>
                    <ContentCopyRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          );
        })}
      </Paper>

      <Stack spacing={1.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
        {products.map((product) => {
          const status = statusPresentation(product.status || 'published');
          return (
            <Card key={product.id} sx={{ p: 2 }}>
              <Stack direction="row" gap={1.75}>
                <ProductImage product={product} size={82} to={`/productDetails/${product.id}`} />
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography fontWeight={700}>{product.name_bg}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {product.code} · #{product.id}
                  </Typography>
                  <Chip
                    label={status.label}
                    color={status.color}
                    size="small"
                    sx={{ bgcolor: status.background, color: status.textColor, mt: 1 }}
                  />
                </Box>
              </Stack>
              <Stack direction="row" gap={1} mt={2}>
                <Button
                  fullWidth
                  variant="contained"
                  component={Link}
                  to={`/productDetails/${product.id}`}
                  startIcon={<EditRoundedIcon />}
                >
                  Редактирай
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  component={Link}
                  to={`/cloneProduct/${product.id}`}
                  startIcon={<ContentCopyRoundedIcon />}
                >
                  Клонирай
                </Button>
              </Stack>
            </Card>
          );
        })}
      </Stack>

      {!products.length && !isLoading && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <Inventory2RoundedIcon sx={{ color: 'grey.300', fontSize: 48 }} />
          <Typography variant="h6" sx={{ mt: 1 }}>
            Няма намерени продукти
          </Typography>
          <Typography color="text.secondary">Променете филтрите и опитайте отново.</Typography>
        </Paper>
      )}

      {products.length > 0 && hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="outlined"
            endIcon={<KeyboardArrowDownRoundedIcon />}
            onClick={loadMore}
            disabled={isLoading}
          >
            {isLoading ? 'Зареждане...' : 'Зареди още'}
          </Button>
        </Box>
      )}
    </VendorDashboardLayout>
  );
}
